"use client";

import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Modal,
  Form,
  Row,
  Col,
  Select,
  InputNumber,
  Button,
  Table,
  Typography,
  Space,
  Input,
  Steps,
} from "antd";
import { PlusOutlined, DeleteOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { apiRequest } from "../../../../service/apiRequest";
import ProductModal from "../../products/handicraft-products/ProductModal";
import { Products } from "../../../../types/CreateProductType";
import {
  calculateCFT,
  getWidthQtyTotal,
} from "../../../../utils/cftCalculator";
import {
  fetchUsedBomProductIds,
  fetchWoodData,
  fetchWoodTypeData,
  fetcProductData,
  fetcunittypeData,
} from "../../../../redux/slice/masterSlice";
import { Wood } from "../../../../types/Wood";
import { WoodType } from "../../../../types/WoodType";
import { showError } from "../../../../components/errorHandler";

const { Text } = Typography;

/* ---------------- TYPES ---------------- */

type WoodRow = {
  key: string;
  bwr_wood_id: number | null;
  bwr_wood_type_id: number | null;
  bwr_widths: { [width: number]: number };
  bwr_rate: number;
};

type ComponentItem = {
  component_key: string;
  component_name: string;
  component_qty: number;
  woodRows: WoodRow[];
};

type SectionRow = {
  key: string;
  component_key: string;
  bsr_product_id: number | null;
  bsr_qty: number;
  bsr_price: number;
};

type BomModalProps = {
  visible: boolean;
  onCancel: () => void;
  onCreate: () => Promise<void>;
  initialValues?: any;
  isEditMode?: boolean;
};

/* ---------------- CONSTANTS & HELPERS ---------------- */

const woodWidths = [1, ...Array.from({ length: 18 }, (_, i) => i + 3)];
const ALL_WIDTHS = Array.from({ length: 18 }, (_, i) => i + 3); // [3, 4, ..., 20]

const SECTION_KEYS = [
  "Machinery",
  "Assembly",
  "Sanding",
  "Finishing",
  "Packed",
] as const;

const STEPS = [
  { title: "Timber", description: "Wood components" },
  { title: "Machinery", description: "Machine parts" },
  { title: "Assembly", description: "Assembly items" },
  { title: "Sanding", description: "Dispatch items" },
  { title: "Finishing", description: "Final touches" },
  { title: "Packed", description: "Packaging" },
];

/**
 * Sanitizes floating point numbers to clean representation (up to 4 decimal places)
 * Prevents IEEE-754 binary floating-point artifacts like 0.30000001192 or 0.0000300001
 */
export const cleanNumber = (val: any): number => {
  if (val === null || val === undefined || val === "") return 0;
  const num = Number(val);
  if (isNaN(num)) return 0;
  return parseFloat(num.toFixed(4));
};

/* ---------------- WOOD TABLE ---------------- */

interface WoodTableProps {
  woods: any[];
  woodtypes: any[];
  woodRows: WoodRow[];
  setWoodRows: React.Dispatch<React.SetStateAction<WoodRow[]>>;
  activeWidths: number[];
  setActiveWidths: (widths: number[] | ((prev: number[]) => number[])) => void;
  componentQty: number;
  setComponentQty: (qty: number) => void;
}

const WoodTable: React.FC<WoodTableProps> = ({
  woods,
  woodtypes,
  woodRows,
  setWoodRows,
  activeWidths,
  setActiveWidths,
  componentQty,
  setComponentQty,
}) => {
  const addRow = () => {
    const emptyWidths = woodWidths.reduce((acc, w) => ({ ...acc, [w]: 0 }), {});
    setWoodRows((prev) => [
      ...prev,
      {
        key: Date.now().toString(),
        bwr_wood_id: null,
        bwr_wood_type_id: null,
        bwr_widths: emptyWidths,
        bwr_rate: 0,
      },
    ]);
  };

  const removeRow = (key: string) =>
    setWoodRows((prev) => prev.filter((r) => r.key !== key));

  const updateRow = (
    key: string,
    field: keyof WoodRow,
    value: any,
    width?: number,
  ) => {
    setWoodRows((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        if (field === "bwr_widths" && width !== undefined)
          return {
            ...r,
            bwr_widths: {
              ...r.bwr_widths,
              [width]: value !== null && value !== undefined ? cleanNumber(value) : 0,
            },
          };
        return {
          ...r,
          [field]: value !== null && value !== undefined ? cleanNumber(value) : 0,
        };
      }),
    );
  };

  // Compute component wood total amount
  const compTotalWoodAmount = useMemo(() => {
    const compQty = Number(componentQty) || 1;
    return woodRows.reduce((sum, record) => {
      const wood = woods.find(
        (w: any) => Number(w.wood_id ?? w.id) === Number(record.bwr_wood_id),
      ) as Wood | undefined;
      if (!wood) return sum;
      const widthQtyTotal = getWidthQtyTotal(record.bwr_widths) * compQty;
      const cft = calculateCFT(
        widthQtyTotal,
        Number(wood.wood_length),
        Number(wood.wood_thickness),
      );
      return sum + cft * (Number(record.bwr_rate) || 0);
    }, 0);
  }, [woodRows, woods, componentQty]);

  const columns: any[] = [
    {
      title: "Wood",
      fixed: "left" as const,
      width: 160,
      render: (_: any, record: WoodRow) => (
        <Select
          value={
            record.bwr_wood_id !== null && record.bwr_wood_id !== undefined
              ? Number(record.bwr_wood_id)
              : undefined
          }
          showSearch
          optionFilterProp="label"
          placeholder="Select Wood"
          onChange={(val) =>
            updateRow(
              record.key,
              "bwr_wood_id",
              val !== undefined && val !== null ? Number(val) : null,
            )
          }
          style={{ width: 150 }}
          options={woods.map((w: any) => ({
            label:
              w.wood_name ||
              w.name ||
              (w.wood_length && w.wood_thickness
                ? `${w.wood_length}' x ${w.wood_thickness}"`
                : `Wood #${w.wood_id ?? w.id}`),
            value: Number(w.wood_id ?? w.id),
          }))}
          allowClear
        />
      ),
    },
    {
      title: "Wood Type",
      fixed: "left" as const,
      width: 160,
      render: (_: any, record: WoodRow) => (
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Select Type"
          value={
            record.bwr_wood_type_id !== null && record.bwr_wood_type_id !== undefined
              ? Number(record.bwr_wood_type_id)
              : undefined
          }
          onChange={(val) =>
            updateRow(
              record.key,
              "bwr_wood_type_id",
              val !== undefined && val !== null ? Number(val) : null,
            )
          }
          style={{ width: 150 }}
          options={woodtypes.map((w: any) => ({
            label:
              w.wood_type_name ||
              w.wood_name ||
              w.name ||
              `Type #${w.wood_type_id ?? w.wood_id ?? w.id}`,
            value: Number(w.wood_type_id ?? w.wood_id ?? w.id),
          }))}
          allowClear
        />
      ),
    },
    {
      title: "Mix",
      width: 70,
      render: (_: any, record: WoodRow) => (
        <InputNumber
          min={0}
          step={0.01}
          style={{ width: 62 }}
          value={cleanNumber(record.bwr_widths[1])}
          onChange={(val) => updateRow(record.key, "bwr_widths", val, 1)}
        />
      ),
    },
    ...activeWidths.map((width) => ({
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 4,
          }}
        >
          <span>{width}"</span>
          <Button
            type="text"
            size="small"
            danger
            icon={<CloseCircleOutlined style={{ fontSize: 13, color: "#ff4d4f" }} />}
            style={{ padding: 0, width: 16, height: 16, minWidth: 16, lineHeight: 1 }}
            title={`Remove ${width}" column`}
            onClick={(e) => {
              e.stopPropagation();
              // Remove this width column
              setActiveWidths((prev) => prev.filter((w) => w !== width));
              // Reset values for this width in all rows of this component
              setWoodRows((prev) =>
                prev.map((r) => ({
                  ...r,
                  bwr_widths: { ...r.bwr_widths, [width]: 0 },
                })),
              );
            }}
          />
        </div>
      ),
      width: 80,
      render: (_: any, record: WoodRow) => (
        <InputNumber
          min={0}
          step={0.01}
          style={{ width: 65 }}
          value={cleanNumber(record.bwr_widths[width])}
          onChange={(val) => updateRow(record.key, "bwr_widths", val, width)}
        />
      ),
    })),
    {
      title: "Total Inch",
      width: 95,
      render: (_: any, record: WoodRow) => {
        const compQty = Number(componentQty) || 1;
        const widthQtyTotal = getWidthQtyTotal(record.bwr_widths);
        const totalInches = cleanNumber(widthQtyTotal * compQty);
        return <Text strong>{totalInches ? `${totalInches}"` : 0}</Text>;
      },
    },
    {
      title: "CFT",
      width: 100,
      render: (_: any, record: WoodRow) => {
        const wood = woods.find(
          (w: any) => Number(w.wood_id ?? w.id) === Number(record.bwr_wood_id),
        ) as Wood | undefined;
        if (!wood) return <span style={{ color: "#aaa" }}>-</span>;

        const compQty = Number(componentQty) || 1;
        const widthQtyTotal = getWidthQtyTotal(record.bwr_widths) * compQty;
        const cft = calculateCFT(
          widthQtyTotal,
          Number(wood.wood_length),
          Number(wood.wood_thickness),
        );
        return <strong>{cleanNumber(cft)}</strong>;
      },
    },
    {
      title: "Rate",
      width: 100,
      render: (_: any, record: WoodRow) => (
        <InputNumber
          min={0}
          step={0.01}
          style={{ width: 85 }}
          value={cleanNumber(record.bwr_rate)}
          onChange={(val) => updateRow(record.key, "bwr_rate", val)}
        />
      ),
    },
    {
      title: "Amount",
      width: 110,
      render: (_: any, record: WoodRow) => {
        const wood = woods.find(
          (w: any) => Number(w.wood_id ?? w.id) === Number(record.bwr_wood_id),
        ) as Wood | undefined;
        const compQty = Number(componentQty) || 1;
        const widthQtyTotal = getWidthQtyTotal(record.bwr_widths) * compQty;
        const cft = wood
          ? calculateCFT(
            widthQtyTotal,
            Number(wood.wood_length),
            Number(wood.wood_thickness),
          )
          : 0;
        const rowAmount = cleanNumber(cft * record.bwr_rate);
        return <Text strong>{rowAmount.toFixed(4)}</Text>;
      },
    },
    {
      title: "",
      width: 50,
      fixed: "right" as const,
      render: (_: any, record: WoodRow) => (
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeRow(record.key)}
        />
      ),
    },
  ];

  const availableWidthOptions = ALL_WIDTHS.filter(
    (w) => !activeWidths.includes(w),
  );

  return (
    <div style={{ marginBottom: 16, marginTop: 16 }}>
      <div
        style={{
          marginBottom: 8,
          display: "flex",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Button type="dashed" icon={<PlusOutlined />} onClick={addRow}>
          Add Wood Row
        </Button>
        <Space align="center">
          <Text strong style={{ fontSize: 13, color: "#444" }}>
            Component Qty:
          </Text>
          <InputNumber
            min={1}
            step={1}
            value={componentQty ?? 1}
            onChange={(val) => setComponentQty(val ?? 1)}
            style={{ width: 80 }}
          />
        </Space>
        {availableWidthOptions.length > 0 && (
          <Select
            placeholder="+ Add Width Column"
            style={{ width: 180 }}
            value={null}
            onChange={(val) => {
              if (val) {
                setActiveWidths((prev) => [...prev, val].sort((a, b) => a - b));
              }
            }}
            options={availableWidthOptions.map((w) => ({
              label: `${w}" Width`,
              value: w,
            }))}
          />
        )}
      </div>

      <Table
        columns={columns}
        dataSource={woodRows}
        rowKey="key"
        pagination={false}
        scroll={{ x: "max-content" }}
        size="small"
      />

      {/* Total Amount of Component After Wood Table */}
      <div
        style={{
          marginTop: 10,
          padding: "8px 16px",
          background: "#f6ffed",
          border: "1px solid #b7eb8f",
          borderRadius: 6,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Text style={{ fontSize: 13, color: "#389e0d", fontWeight: 500 }}>
          Total Component Amount:
        </Text>
        <Text strong style={{ fontSize: 15, color: "#237804" }}>
          ₹ {cleanNumber(compTotalWoodAmount).toFixed(4)}
        </Text>
      </div>
    </div>
  );
};

/* ---------------- SECTION TABLE ---------------- */

interface SectionTableProps {
  products: any[];
  data: SectionRow[];
  setData: (
    rows: SectionRow[] | ((prev: SectionRow[]) => SectionRow[]),
  ) => void;
  components: ComponentItem[];
  isSingleSection?: boolean;
  sectionTitle?: string;
}

const SectionTable: React.FC<SectionTableProps> = ({
  products,
  data,
  setData,
  components,
  isSingleSection = false,
  sectionTitle,
}) => {
  const { unittypes } = useAppSelector((state) => state.master);

  const addRow = (component_key: string) => {
    setData((prev) => [
      ...prev,
      {
        key: Date.now().toString(),
        component_key,
        bsr_product_id: null,
        bsr_qty: 0,
        bsr_price: 0,
      },
    ]);
  };

  const removeRow = (key: string) =>
    setData((prev) => prev.filter((r) => r.key !== key));

  const updateRow = (key: string, field: keyof SectionRow, value: any) =>
    setData((prev) =>
      prev.map((r) =>
        r.key === key
          ? {
            ...r,
            [field]:
              value !== null && value !== undefined ? cleanNumber(value) : 0,
          }
          : r,
      ),
    );

  const displayedComponents = isSingleSection
    ? components.length > 0
      ? [components[0]]
      : []
    : components;

  return (
    <div>
      {components.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 13 }}>
          No components added yet. Go to the Timber step to add components
          first.
        </Text>
      ) : (
        displayedComponents.map((comp) => {
          const compRows = (data || []).filter(
            (r) => r.component_key === comp.component_key,
          );
          const compSectionTotal = compRows.reduce(
            (sum, r) =>
              sum + (Number(r.bsr_qty) || 0) * (Number(r.bsr_price) || 0),
            0,
          );

          return (
            <div
              key={comp.component_key}
              style={{
                marginBottom: 16,
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                padding: "12px 16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 10,
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Text strong style={{ fontSize: 14 }}>
                    {isSingleSection
                      ? sectionTitle || "Assembly"
                      : comp.component_name || "(Unnamed Component)"}
                  </Text>
                  {!isSingleSection && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      (Qty: {comp.component_qty ?? 1})
                    </Text>
                  )}
                  <Button
                    type="dashed"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => addRow(comp.component_key)}
                  >
                    Add Product Row
                  </Button>
                </div>
              </div>

              <Table
                size="small"
                pagination={false}
                rowKey="key"
                dataSource={compRows}
                columns={[
                  {
                    title: "Product",
                    render: (_: any, record: SectionRow) => (
                      <Select
                        showSearch
                        optionFilterProp="label"
                        value={record.bsr_product_id}
                        onChange={(val) => {
                          const prod = products.find((p) => p.product_id === val);
                          setData((prev) =>
                            prev.map((r) =>
                              r.key === record.key
                                ? {
                                  ...r,
                                  bsr_product_id: val,
                                  bsr_price: cleanNumber(
                                    prod?.last_purchase_price ||
                                    prod?.product_price ||
                                    r.bsr_price ||
                                    0,
                                  ),
                                }
                                : r,
                            ),
                          );
                        }}
                        style={{ width: 200 }}
                        allowClear
                        options={products.map((p) => ({
                          label: p.product_name,
                          value: p.product_id,
                        }))}
                      />
                    ),
                  },
                  {
                    title: "Qty",
                    width: 105,
                    render: (_: any, record: SectionRow) => (
                      <InputNumber
                        min={0}
                        step={0.01}
                        value={cleanNumber(record.bsr_qty)}
                        onChange={(val) => updateRow(record.key, "bsr_qty", val)}
                      />
                    ),
                  },
                  {
                    title: "Unit Type",
                    width: 120,
                    render: (_: any, record: SectionRow) => {
                      const prod = products.find(
                        (p) => p.product_id === record.bsr_product_id,
                      );
                      const unit = unittypes?.find(
                        (u) => u.ut_id === (prod?.ut_id || prod?.product_unit),
                      );
                      const unitName = unit?.ut_name || prod?.ut_name || "-";
                      return (
                        <Text style={{ fontWeight: 600, color: "#555" }}>
                          {unitName}
                        </Text>
                      );
                    },
                  },
                  {
                    title: "Price",
                    width: 120,
                    render: (_: any, record: SectionRow) => (
                      <InputNumber
                        min={0}
                        step={0.01}
                        value={cleanNumber(record.bsr_price)}
                        onChange={(val) =>
                          updateRow(record.key, "bsr_price", val)
                        }
                      />
                    ),
                  },
                  {
                    title: "Amount",
                    width: 110,
                    render: (_: any, record: SectionRow) => (
                      <Text strong>
                        {cleanNumber(record.bsr_qty * record.bsr_price).toFixed(
                          4,
                        )}
                      </Text>
                    ),
                  },
                  {
                    title: "Action",
                    width: 80,
                    render: (_: any, record: SectionRow) => (
                      <Button
                        danger
                        size="small"
                        onClick={() => removeRow(record.key)}
                      >
                        Delete
                      </Button>
                    ),
                  },
                ]}
              />

              {/* Total Amount of Component After Section Table */}
              <div
                style={{
                  marginTop: 10,
                  padding: "8px 16px",
                  background: "#f6ffed",
                  border: "1px solid #b7eb8f",
                  borderRadius: 6,
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Text style={{ fontSize: 13, color: "#389e0d", fontWeight: 500 }}>
                  Total Component Amount:
                </Text>
                <Text strong style={{ fontSize: 15, color: "#237804" }}>
                  ₹ {cleanNumber(compSectionTotal).toFixed(4)}
                </Text>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

/* ---------------- STEP CONTENT ---------------- */

interface StepContentProps {
  step: number;
  products: any[];
  woods: any[];
  woodtypes: any[];
  components: ComponentItem[];
  setComponents: React.Dispatch<React.SetStateAction<ComponentItem[]>>;
  sections: Record<string, SectionRow[]>;
  setSections: React.Dispatch<
    React.SetStateAction<Record<string, SectionRow[]>>
  >;
  removeComponent: (key: string) => void;
  addComponent: () => void;
  activeWidthsByComp: Record<string, number[]>;
  setActiveWidthsByComp: React.Dispatch<
    React.SetStateAction<Record<string, number[]>>
  >;
}

const StepContent: React.FC<StepContentProps> = ({
  step,
  products,
  woods,
  woodtypes,
  components,
  setComponents,
  sections,
  setSections,
  removeComponent,
  addComponent,
  activeWidthsByComp,
  setActiveWidthsByComp,
}) => {
  /* ── Step 0: Timber ── */
  if (step === 0) {
    return (
      <div>
        {components.map((comp) => (
          <div
            key={comp.component_key}
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              padding: "12px 16px",
              marginBottom: 16,
            }}
          >
            <Row gutter={12} align="middle" style={{ marginBottom: 8 }}>
              <Col>
                <Input
                  placeholder="Component Name"
                  value={comp.component_name}
                  onChange={(e) =>
                    setComponents((prev) =>
                      prev.map((c) =>
                        c.component_key === comp.component_key
                          ? { ...c, component_name: e.target.value }
                          : c,
                      ),
                    )
                  }
                  style={{ width: 220 }}
                />
              </Col>
              <Col>
                <Button
                  danger
                  onClick={() => removeComponent(comp.component_key)}
                >
                  Delete Component
                </Button>
              </Col>
            </Row>
            <WoodTable
              woods={woods}
              woodtypes={woodtypes}
              woodRows={comp.woodRows}
              setWoodRows={(newRows) => {
                const updatedRows =
                  typeof newRows === "function"
                    ? newRows(comp.woodRows)
                    : newRows;
                setComponents((prev) =>
                  prev.map((c) =>
                    c.component_key === comp.component_key
                      ? { ...c, woodRows: updatedRows }
                      : c,
                  ),
                );
              }}
              activeWidths={activeWidthsByComp[comp.component_key] || []}
              setActiveWidths={(newWidths) => {
                setActiveWidthsByComp((prev) => {
                  const currentCompWidths = prev[comp.component_key] || [];
                  const resolved =
                    typeof newWidths === "function"
                      ? newWidths(currentCompWidths)
                      : newWidths;
                  return {
                    ...prev,
                    [comp.component_key]: resolved,
                  };
                });
              }}
              componentQty={comp.component_qty ?? 1}
              setComponentQty={(qty) => {
                setComponents((prev) =>
                  prev.map((c) =>
                    c.component_key === comp.component_key
                      ? { ...c, component_qty: qty }
                      : c,
                  ),
                );
              }}
            />
          </div>
        ))}
        <Button type="dashed" icon={<PlusOutlined />} onClick={addComponent}>
          Add Component
        </Button>
      </div>
    );
  }

  /* ── Steps 1-6: Sections ── */
  const sectionKey = SECTION_KEYS[step - 1];

  return (
    <SectionTable
      products={products}
      data={sections[sectionKey] || []}
      setData={(rows) =>
        setSections((prev) => ({
          ...prev,
          [sectionKey]:
            typeof rows === "function" ? rows(prev[sectionKey] || []) : rows,
        }))
      }
      components={components}
      isSingleSection={step > 2}
      sectionTitle={sectionKey}
    />
  );
};

/* ---------------- MAIN BOM MODAL ---------------- */

const BomModal: React.FC<BomModalProps> = ({
  visible,
  onCancel,
  onCreate,
  initialValues,
  isEditMode,
}) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();

  const {
    products,
    woods,
    woodtypes,
    usedBomProductIds = [],
  } = useAppSelector((state) => state.master);

  const [localWoods, setLocalWoods] = useState<Wood[]>([]);
  const [localWoodTypes, setLocalWoodTypes] = useState<WoodType[]>([]);
  const [localProducts, setLocalProducts] = useState<Products[]>([]);

  const effectiveWoods: any[] = (woods && woods.length > 0 ? (woods as any[]) : localWoods) || [];
  const effectiveWoodTypes: any[] =
    (woodtypes && woodtypes.length > 0 ? (woodtypes as any[]) : localWoodTypes) || [];
  const effectiveProducts: any[] =
    (products && products.length > 0 ? (products as any[]) : localProducts) || [];

  const [currentStep, setCurrentStep] = useState(0);
  const [components, setComponents] = useState<ComponentItem[]>([]);
  const [sections, setSections] = useState<Record<string, SectionRow[]>>({
    Machinery: [],
    Assembly: [],
    Sanding: [],
    Finishing: [],
    Packed: [],
  });
  const [isProductModalVisible, setIsProductModalVisible] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [activeWidthsByComp, setActiveWidthsByComp] = useState<
    Record<string, number[]>
  >({});

  // Scroll step container back to top whenever the step changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [currentStep]);

  const handleProductCreated = (_newProduct: Products) => {
    setIsProductModalVisible(false);
  };

  // Direct fetch loader for modal
  const loadMasterDataDirectly = async () => {
    try {
      const woodPromise = (async () => {
        try {
          const res = await apiRequest<Wood[]>({
            method: "get",
            url: "woods?status=1",
            silent: true,
          });
          if (Array.isArray(res) && res.length > 0) return res;
          const fallback = await apiRequest<Wood[]>({
            method: "get",
            url: "woods",
            silent: true,
          });
          return Array.isArray(fallback) ? fallback : [];
        } catch {
          try {
            const fallback = await apiRequest<Wood[]>({
              method: "get",
              url: "woods",
              silent: true,
            });
            return Array.isArray(fallback) ? fallback : [];
          } catch {
            return [];
          }
        }
      })();

      const woodTypePromise = (async () => {
        try {
          const res = await apiRequest<WoodType[]>({
            method: "get",
            url: "wood-types?status=1",
            silent: true,
          });
          if (Array.isArray(res) && res.length > 0) return res;
          const fallback = await apiRequest<WoodType[]>({
            method: "get",
            url: "wood-types",
            silent: true,
          });
          return Array.isArray(fallback) ? fallback : [];
        } catch {
          try {
            const fallback = await apiRequest<WoodType[]>({
              method: "get",
              url: "wood-types",
              silent: true,
            });
            return Array.isArray(fallback) ? fallback : [];
          } catch {
            return [];
          }
        }
      })();

      const productPromise = (async () => {
        try {
          const res = await apiRequest<any>({
            method: "get",
            url: "get-all-handicraft-products?status=1",
            silent: true,
          });
          return (res as any)?.data || (Array.isArray(res) ? res : []);
        } catch {
          try {
            const res = await apiRequest<any>({
              method: "get",
              url: "get-all-handicraft-products",
              silent: true,
            });
            return (res as any)?.data || (Array.isArray(res) ? res : []);
          } catch {
            return [];
          }
        }
      })();

      const [woodsRes, woodTypesRes, productsRes] = await Promise.all([
        woodPromise,
        woodTypePromise,
        productPromise,
      ]);

      if (woodsRes && woodsRes.length > 0) setLocalWoods(woodsRes);
      if (woodTypesRes && woodTypesRes.length > 0)
        setLocalWoodTypes(woodTypesRes);
      if (productsRes && productsRes.length > 0)
        setLocalProducts(productsRes);
    } catch (e) {
      console.error("Direct fetch master error in BomModal:", e);
    }
  };

  useEffect(() => {
    if (visible) {
      dispatch(fetchUsedBomProductIds());
      dispatch(fetchWoodData());
      dispatch(fetchWoodTypeData());
      dispatch(fetcProductData());
      dispatch(fetcunittypeData());
      loadMasterDataDirectly();
    }
  }, [visible, dispatch]);

  const availableProducts = effectiveProducts.filter((p) => {
    if (initialValues?.bom_product_id === p.product_id) return true;
    return !(usedBomProductIds ?? []).includes(p.product_id);
  });

  /* ── Reset / populate on open ── */
  useEffect(() => {
    if (!visible) return;
    setCurrentStep(0);

    if (initialValues) {
      form.setFieldsValue({ product_id: initialValues.bom_product_id });

      const widthsByComp: Record<string, number[]> = {};

      const compsWithKeys = (initialValues.components || []).map((c: any) => {
        const compKey = (c.component_id ?? c.component_key ?? Date.now()).toString();
        const compWidths = new Set<number>();

        const woodRows = (c.woodRows || []).map((w: any) => {
          let parsedWidths: Record<string, number> = {};
          try {
            parsedWidths =
              typeof w.bwr_widths === "string"
                ? JSON.parse(w.bwr_widths || "{}")
                : w.bwr_widths || {};
          } catch (e) {
            parsedWidths = {};
          }

          const cleanedWidths: { [width: number]: number } = {};
          woodWidths.forEach((widthNum) => {
            cleanedWidths[widthNum] = cleanNumber(parsedWidths[widthNum] ?? 0);
          });

          Object.entries(parsedWidths).forEach(([wKey, val]) => {
            const wNum = Number(wKey);
            const cleanVal = cleanNumber(val);
            cleanedWidths[wNum] = cleanVal;
            if (wNum > 1 && cleanVal > 0) {
              compWidths.add(wNum);
            }
          });

          return {
            key: (w.bwr_id || Date.now() + Math.random()).toString(),
            bwr_wood_id:
              w.bwr_wood_id !== undefined && w.bwr_wood_id !== null
                ? Number(w.bwr_wood_id)
                : w.wood_id !== undefined && w.wood_id !== null
                  ? Number(w.wood_id)
                  : null,
            bwr_wood_type_id:
              w.bwr_wood_type_id !== undefined && w.bwr_wood_type_id !== null
                ? Number(w.bwr_wood_type_id)
                : w.wood_type_id !== undefined && w.wood_type_id !== null
                  ? Number(w.wood_type_id)
                  : null,
            bwr_widths: cleanedWidths,
            bwr_rate: cleanNumber(w.bwr_rate),
          };
        });

        widthsByComp[compKey] = Array.from(compWidths).sort((a, b) => a - b);

        return {
          component_key: compKey,
          component_name: c.component_name || "",
          component_qty: Number(c.component_qty ?? 1) || 1,
          woodRows,
        };
      });

      setComponents(compsWithKeys);
      setActiveWidthsByComp(widthsByComp);

      const mapSection = (arr: any[]) =>
        (arr || []).map((r: any) => ({
          ...r,
          key: r.bsr_id?.toString() || (Date.now() + Math.random()).toString(),
          component_key: String(
            r.component_key ??
            r.bsr_component_key ??
            r.bsr_component_id ??
            r.component_id ??
            "",
          ),
          bsr_product_id:
            r.bsr_product_id !== undefined && r.bsr_product_id !== null
              ? Number(r.bsr_product_id)
              : null,
          bsr_qty: cleanNumber(r.bsr_qty),
          bsr_price: cleanNumber(r.bsr_price),
        }));

      setSections({
        Machinery: mapSection(initialValues.Machinery),
        Assembly: mapSection(initialValues.Assembly),
        Sanding: mapSection(initialValues.Sanding),
        Finishing: mapSection(initialValues.Finishing),
        Packed: mapSection(initialValues.Packed),
      });
    } else {
      form.resetFields();
      setComponents([]);
      setActiveWidthsByComp({});
      setSections({
        Machinery: [],
        Assembly: [],
        Sanding: [],
        Finishing: [],
        Packed: [],
      });
    }
  }, [visible, initialValues, form]);

  const addComponent = () => {
    const newKey = Date.now().toString();
    setComponents((prev) => [
      ...prev,
      {
        component_key: newKey,
        component_name: "",
        component_qty: 1,
        woodRows: [],
      },
    ]);
    setActiveWidthsByComp((prev) => ({ ...prev, [newKey]: [] }));
  };

  const removeComponent = (component_key: string) => {
    setComponents((prev) =>
      prev.filter((c) => c.component_key !== component_key),
    );
    setActiveWidthsByComp((prev) => {
      const next = { ...prev };
      delete next[component_key];
      return next;
    });
    SECTION_KEYS.forEach((sec) =>
      setSections((prev) => ({
        ...prev,
        [sec]: prev[sec].filter((r) => r.component_key !== component_key),
      })),
    );
  };

  const validateCurrentStep = () => {
    if (!isEditMode) {
      const prodId = form.getFieldValue("product_id");
      if (prodId === undefined || prodId === null || prodId === "") {
        showError("Please select a product first.");
        return false;
      }
    }

    if (currentStep === 0) {
      for (const comp of components) {
        for (const row of comp.woodRows) {
          if (
            row.bwr_wood_id === undefined ||
            row.bwr_wood_id === null ||
            row.bwr_wood_type_id === undefined ||
            row.bwr_wood_type_id === null
          ) {
            showError(
              `Please select Wood and Wood Type for all rows in "${comp.component_name || "Component"}", or remove the incomplete row.`,
            );
            return false;
          }
        }
      }
    } else {
      const sectionKey = SECTION_KEYS[currentStep - 1];
      const rows = sections[sectionKey] || [];
      for (const row of rows) {
        if (row.bsr_product_id === undefined || row.bsr_product_id === null) {
          showError(
            `Please select a Product for all rows in "${sectionKey}", or remove the incomplete row.`,
          );
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (values: any) => {
    if (!isEditMode && !values.product_id) {
      showError("Please select a product first.");
      return;
    }

    for (const comp of components) {
      for (const row of comp.woodRows) {
        if (
          row.bwr_wood_id === undefined ||
          row.bwr_wood_id === null ||
          row.bwr_wood_type_id === undefined ||
          row.bwr_wood_type_id === null
        ) {
          showError(
            `Please select Wood and Wood Type for all rows in "${comp.component_name || "Component"}", or remove the incomplete row.`,
          );
          return;
        }
      }
    }

    for (const sec of SECTION_KEYS) {
      const rows = sections[sec] || [];
      for (const row of rows) {
        if (row.bsr_product_id === undefined || row.bsr_product_id === null) {
          showError(
            `Please select a Product for all rows in "${sec}", or remove the incomplete row.`,
          );
          return;
        }
      }
    }

    try {
      const sanitizedComponents = components.map((comp) => ({
        component_key: comp.component_key ?? null,
        component_name: comp.component_name ?? "",
        component_qty: Number(comp.component_qty ?? 1) || 1,
        woodRows: comp.woodRows.map((row) => ({
          key: row.key ?? null,
          bwr_wood_id: row.bwr_wood_id ?? null,
          bwr_wood_type_id: row.bwr_wood_type_id ?? null,
          bwr_widths: row.bwr_widths,
          bwr_rate: cleanNumber(row.bwr_rate),
        })),
      }));

      const sanitizedSections: Record<string, any[]> = {};
      SECTION_KEYS.forEach((sec) => {
        sanitizedSections[sec] = (sections[sec] || []).map((row) => ({
          component_key: row.component_key ?? null,
          bsr_product_id: row.bsr_product_id ?? null,
          bsr_qty: cleanNumber(row.bsr_qty),
          bsr_price: cleanNumber(row.bsr_price),
        }));
      });

      const payload = {
        bom_id: initialValues?.bom_id ?? null,
        product_id: values.product_id || initialValues?.bom_product_id,
        components: sanitizedComponents,
        ...sanitizedSections,
      };

      await apiRequest({ method: "post", url: "add-bom", data: payload });
      await onCreate();
      onCancel();
    } catch (err) {
      alert("Failed to submit BOM");
    }
  };

  /* ── Department Totals for Footer Summary ── */
  const timberTotal = useMemo(() => {
    return components.reduce((cSum, comp) => {
      const compQty = Number(comp.component_qty) || 1;
      const compWoodTotal = (comp.woodRows || []).reduce((wSum, row) => {
        const wood = effectiveWoods.find(
          (w: any) => Number(w.wood_id ?? w.id) === Number(row.bwr_wood_id),
        );
        if (!wood) return wSum;
        const widthQtyTotal = getWidthQtyTotal(row.bwr_widths) * compQty;
        const cft = calculateCFT(
          widthQtyTotal,
          Number(wood.wood_length),
          Number(wood.wood_thickness),
        );
        return wSum + cft * (Number(row.bwr_rate) || 0);
      }, 0);
      return cSum + compWoodTotal;
    }, 0);
  }, [components, effectiveWoods]);

  const machineryTotal = useMemo(() => {
    return (sections["Machinery"] || []).reduce(
      (sum, r) => sum + (Number(r.bsr_qty) || 0) * (Number(r.bsr_price) || 0),
      0,
    );
  }, [sections]);

  const assemblyTotal = useMemo(() => {
    return (sections["Assembly"] || []).reduce(
      (sum, r) => sum + (Number(r.bsr_qty) || 0) * (Number(r.bsr_price) || 0),
      0,
    );
  }, [sections]);

  const sandingTotal = useMemo(() => {
    return (sections["Sanding"] || []).reduce(
      (sum, r) => sum + (Number(r.bsr_qty) || 0) * (Number(r.bsr_price) || 0),
      0,
    );
  }, [sections]);

  const finishingTotal = useMemo(() => {
    return (sections["Finishing"] || []).reduce(
      (sum, r) => sum + (Number(r.bsr_qty) || 0) * (Number(r.bsr_price) || 0),
      0,
    );
  }, [sections]);

  const packedTotal = useMemo(() => {
    return (sections["Packed"] || []).reduce(
      (sum, r) => sum + (Number(r.bsr_qty) || 0) * (Number(r.bsr_price) || 0),
      0,
    );
  }, [sections]);

  const grandTotal = useMemo(() => {
    return (
      timberTotal +
      machineryTotal +
      assemblyTotal +
      sandingTotal +
      finishingTotal +
      packedTotal
    );
  }, [
    timberTotal,
    machineryTotal,
    assemblyTotal,
    sandingTotal,
    finishingTotal,
    packedTotal,
  ]);

  return (
    <Modal
      open={visible}
      centered
      onCancel={onCancel}
      width="95%"
      title="Bill of Material (B.O.M)"
      footer={null}
      destroyOnHidden
      styles={{ body: { padding: 0 } }}
      maskClosable={false}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {/* ── PRODUCT HEADER ── */}
        <div
          style={{
            padding: "12px 24px",
            borderBottom: "1px solid #f0f0f0",
            background: "#fafafa",
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <Text strong style={{ fontSize: 14, whiteSpace: "nowrap" }}>
            Select Product
          </Text>

          {isEditMode ? (
            <div
              style={{
                padding: "4px 12px",
                background: "#f0f0f0",
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 600,
                color: "#333",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              🔒{" "}
              {products.find(
                (p) => p.product_id === initialValues?.bom_product_id,
              )?.product_name || "—"}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flex: "0 0 340px",
              }}
            >
              <Form.Item
                name="product_id"
                rules={[{ required: true, message: "Please select a product" }]}
                style={{ marginBottom: 0, flex: 1 }}
              >
                <Select
                  showSearch
                  placeholder="Select product"
                  style={{ width: "100%", borderRadius: "6px 0 0 6px" }}
                  allowClear
                  optionFilterProp="label"
                  options={availableProducts.map((p) => ({
                    label: p.product_name,
                    value: p.product_id,
                  }))}
                />
              </Form.Item>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsProductModalVisible(true)}
                style={{
                  borderRadius: "0 6px 6px 0",
                  marginLeft: -1,
                  height: 32,
                }}
              />
            </div>
          )}
        </div>

        <ProductModal
          visible={isProductModalVisible}
          onCancel={() => setIsProductModalVisible(false)}
          onCreate={handleProductCreated}
          isEditMode={false}
          initialValues={null}
        />

        {/* ── HORIZONTAL STEPPER ── */}
        <div
          style={{
            padding: "16px 24px 0",
            background: "#fafafa",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <Steps
            current={currentStep}
            onChange={(step) => {
              if (step < currentStep || validateCurrentStep()) {
                setCurrentStep(step);
              }
            }}
            size="small"
            items={STEPS.map((s) => ({
              title: s.title,
              description: s.description,
            }))}
          />
        </div>

        {/* ── STEP CONTENT ── */}
        <div
          ref={scrollContainerRef}
          style={{ height: "52vh", overflowY: "auto", padding: "20px 28px" }}
        >
          <StepContent
            step={currentStep}
            products={effectiveProducts}
            woods={effectiveWoods}
            woodtypes={effectiveWoodTypes}
            components={components}
            setComponents={setComponents}
            sections={sections}
            setSections={setSections}
            removeComponent={removeComponent}
            addComponent={addComponent}
            activeWidthsByComp={activeWidthsByComp}
            setActiveWidthsByComp={setActiveWidthsByComp}
          />
        </div>

        {/* ── FOOTER ── */}
        <div
          style={{
            borderTop: "1px solid #f0f0f0",
            padding: "12px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#fff",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Text type="secondary" style={{ fontSize: 13, whiteSpace: "nowrap" }}>
              Step {currentStep + 1} of {STEPS.length}
            </Text>
          </div>

          {/* Department-Wise Totals Display Before Cancel Button */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              justifyContent: "flex-end",
              flex: 1,
            }}
          >
            <Space wrap size={[6, 6]}>
              <span
                style={{
                  padding: "3px 8px",
                  background: currentStep === 0 ? "#e6f4ff" : "#f5f5f5",
                  border: `1px solid ${currentStep === 0 ? "#91caff" : "#d9d9d9"}`,
                  borderRadius: 4,
                  fontSize: 12,
                }}
              >
                <strong>Timber:</strong> ₹{cleanNumber(timberTotal).toFixed(2)}
              </span>
              <span
                style={{
                  padding: "3px 8px",
                  background: currentStep === 1 ? "#e6f4ff" : "#f5f5f5",
                  border: `1px solid ${currentStep === 1 ? "#91caff" : "#d9d9d9"}`,
                  borderRadius: 4,
                  fontSize: 12,
                }}
              >
                <strong>Machinery:</strong> ₹{cleanNumber(machineryTotal).toFixed(2)}
              </span>
              <span
                style={{
                  padding: "3px 8px",
                  background: currentStep === 2 ? "#e6f4ff" : "#f5f5f5",
                  border: `1px solid ${currentStep === 2 ? "#91caff" : "#d9d9d9"}`,
                  borderRadius: 4,
                  fontSize: 12,
                }}
              >
                <strong>Assembly:</strong> ₹{cleanNumber(assemblyTotal).toFixed(2)}
              </span>
              <span
                style={{
                  padding: "3px 8px",
                  background: currentStep === 3 ? "#e6f4ff" : "#f5f5f5",
                  border: `1px solid ${currentStep === 3 ? "#91caff" : "#d9d9d9"}`,
                  borderRadius: 4,
                  fontSize: 12,
                }}
              >
                <strong>Sanding:</strong> ₹{cleanNumber(sandingTotal).toFixed(2)}
              </span>
              <span
                style={{
                  padding: "3px 8px",
                  background: currentStep === 4 ? "#e6f4ff" : "#f5f5f5",
                  border: `1px solid ${currentStep === 4 ? "#91caff" : "#d9d9d9"}`,
                  borderRadius: 4,
                  fontSize: 12,
                }}
              >
                <strong>Finishing:</strong> ₹{cleanNumber(finishingTotal).toFixed(2)}
              </span>
              <span
                style={{
                  padding: "3px 8px",
                  background: currentStep === 5 ? "#e6f4ff" : "#f5f5f5",
                  border: `1px solid ${currentStep === 5 ? "#91caff" : "#d9d9d9"}`,
                  borderRadius: 4,
                  fontSize: 12,
                }}
              >
                <strong>Packed:</strong> ₹{cleanNumber(packedTotal).toFixed(2)}
              </span>
              <span
                style={{
                  padding: "4px 10px",
                  background: "#1677ff",
                  color: "#fff",
                  borderRadius: 4,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Grand Total: ₹{cleanNumber(grandTotal).toFixed(2)}
              </span>
            </Space>

            <Space>
              <Button htmlType="button" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                htmlType="button"
                disabled={currentStep === 0}
                onClick={() => setCurrentStep((s) => s - 1)}
              >
                ← Previous
              </Button>
              {currentStep < STEPS.length - 1 && (
                <Button
                  type="primary"
                  htmlType="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (validateCurrentStep()) {
                      setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
                    }
                  }}
                >
                  Next →
                </Button>
              )}
              {currentStep === STEPS.length - 1 && (
                <Button type="primary" htmlType="submit">
                  Submit BOM
                </Button>
              )}
            </Space>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default BomModal;
